import {
  Component,
  ElementRef,
  input,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import { AbbButtonComponent } from '@components/abb-button/abb-button.component';
import { TPipe } from '@pipes/t.pipe';
import { ToastService } from '@services/ui/toast.service';

@Component({
  selector: 'abb-avatar-field',
  standalone: true,
  imports: [CommonModule, AbbIconComponent, AbbButtonComponent, TPipe],
  template: `
    <div class="relative w-fit">
      @if(file){
      <div
        class="h-32 w-32 z-[1] rounded-full bg-abb-background bg-center bg-cover mx-auto"
        [ngClass]="{ 'shadow-xl': !!file }"
        [ngStyle]="{ backgroundImage: 'url(' + file + ')' }"
      ></div>
      } @else {
      <div
        class="h-32 w-32 z-[1] rounded-full bg-abb-background bg-center bg-cover mx-auto"
        [ngStyle]="{
          backgroundImage: 'url(/assets/images/avatar-placeholder.png)'
        }"
      ></div>
      }
      <ng-content select="[avatarSuffix]"></ng-content>
      <label
        for="avatar-input-file"
        class="cursor-pointer flex items-center justify-center w-[inherit] mx-auto"
      >
        @if(circularBtn()) {
        <div
          class="absolute h-8 w-8 p-1 bg-abb-primary rounded-full flex items-center justify-center right-0 top-[75%] z-[2] cursor-pointer hover:bg-abb-primary-dark"
        >
          <input
            type="file"
            id="avatar-input-file"
            class="hidden"
            accept="image/*"
            (change)="onFileChange($event)"
          />
          <abb-icon
            id="edit"
            class="text-abb-white"
            [name]="'mat/edit'"
          ></abb-icon>
        </div>
        } @else {
        <input
          #input
          type="file"
          id="avatar-input-file"
          class="hidden"
          accept="image/*"
          (change)="onFileChange($event)"
        />
        <div
          class="flex flex-row flex-wrap gap-3 justify-center items-center pt-3"
        >
          <abb-button
            id="remove-avatar"
            (onclick)="removeAvatar()"
            variant="clean"
            className="px-5 py-1 text-xs font-medium"
            >{{ 'remove' | t }}</abb-button
          >
          <abb-button
            id="upload-avatar"
            (onclick)="uploadAvatar()"
            className="px-5 py-1 text-xs font-medium"
            >{{ 'Upload Avatar' | t }}</abb-button
          >
        </div>
        }
      </label>
    </div>
  `,
  styles: ``,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: AbbAvatarFieldComponent,
    },
  ],
  host: {
    class: 'self-end',
  },
})
export class AbbAvatarFieldComponent implements OnInit, ControlValueAccessor {
  @ViewChild('input') input: ElementRef<HTMLInputElement> | undefined;
  file: string = '';
  circularBtn = input<boolean>(false);

  constructor(public dialog: MatDialog, private toast: ToastService) {}

  ngOnInit(): void {}

  writeValue(_file: string): void {
    this.file = _file;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onChange = (fileUrl: string) => {};

  onTouched = () => {};

  disabled: boolean = false;

  onFileChange(event: any) {
    const files = event.target.files as FileList;

    if (files.length > 0) {
      const file = files[0];
      const maxSize = 1 * 1024 * 1024; // 1 MB limit, adjust as needed

      if (!file.type.startsWith('image/')) {
        this.toast.notifyError('Only image files are allowed.');
        return;
      }

      if (file.size > maxSize) {
        this.toast.notifyError('File size exceeds the maximum limit of 1 MB.');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.file = e.target.result;
        this.onChange(this.file);
      };
      reader.readAsDataURL(file);
      this.resetInput();
    }
  }

  resetInput() {
    const input = document.getElementById(
      'avatar-input-file'
    ) as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  }

  removeAvatar() {
    this.file = '';
    this.onChange(this.file);
  }

  uploadAvatar() {
    this.input?.nativeElement.click();
  }
}
